import React, { Component } from 'react';
import styled from 'styled-components';

import { Link } from 'components/Common';
import BannerContainer from 'components/ImageContainer/BannerContainer';
import VirtualizedList from 'components/VirtualizedList';
import { SimpleRatings } from 'components/Ratings';

import { isiOS, appsPerPage } from 'config';
import androidInstalledCheck from 'inline-icons/android-installed-check.svg';
import iOSInstalledCheck from 'inline-icons/ios-installed-check.svg';

const CLOCKS_PER_ROW = 2;

const StyledList = styled.div`
  background-color: white;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-evenly;
  align-items: flex-start;
`;

const RowItem = styled.div`
  width: calc(50% - 22.5px);
  text-align: center;
  ${({ isFirstRow, isLastRow }) => `
    margin: ${isFirstRow ? 15 : 0}px 0 ${isLastRow ? 15 : 10}px 0;
  `}
`;

const ClockRow = ({ index, style, apps }) => {
  const appIndex = index * CLOCKS_PER_ROW;
  const isFirstRow = index === 0;
  const isLastRow = index === Math.ceil(apps.length / CLOCKS_PER_ROW);
  const clocks = [];
  for (let i = 0; i < CLOCKS_PER_ROW; i++) {
    const clock = apps[appIndex + i];
    clocks.push(
      clock ? (
        <Clock
          key={clock.id}
          clock={clock}
          selected={clock.isInstalledOrInstalling}
          isFirstRow={isFirstRow}
          isLastRow={isLastRow}
        />
      ) : (
        <RowItem
          key={'list-placeholder-item'}
          isFirstRow={isFirstRow}
          isLastRow={isLastRow}
        />
      )
    );
  }

  return <Row style={style}>{clocks}</Row>;
};

const ClockName = styled.div`
  width: 100%;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectedIcon = styled.div`
  position: absolute;
  bottom: 4px;
  left: calc(50% - (36px / ${CLOCKS_PER_ROW}));
  width: 36px;
  height: 18px;
  border-radius: 50px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  background: white;

  > svg {
    bottom: 0;
    height: 16px;
    left: calc(50% - 8px);
    position: absolute;
    width: 16px;
  }
`;

const NameWrapper = styled.div`
  display: flex;
  height: 35px;
  align-items: center;
  flex-direction: column;
`;

const Clock = ({ clock, isFirstRow, isLastRow, selected }) => {
  const [banner] = clock.banners;
  return (
    <RowItem isFirstRow={isFirstRow} isLastRow={isLastRow}>
      <Link
        to={`/clock/${clock.id}`}
        pageDirection="FORWARD"
        data-test-type="clock-link"
        data-test-id={clock.id}
      >
        <BannerContainer
          containerAttr={{
            width: banner.width,
            height: banner.height
          }}
          imageAttr={{
            src: banner.uri,
            alt: clock.name
          }}
        >
          {selected && (
            <SelectedIcon
              dangerouslySetInnerHTML={{
                __html: isiOS ? iOSInstalledCheck : androidInstalledCheck
              }}
            />
          )}
        </BannerContainer>
        <NameWrapper>
          <ClockName>{clock.name}</ClockName>
          <SimpleRatings app={clock} />
        </NameWrapper>
      </Link>
    </RowItem>
  );
};

export const ClockList = ({ apps }) => {
  const rows = [];
  const numRows = Math.ceil(apps.length / CLOCKS_PER_ROW);
  for (let i = 0; i < numRows; i++) {
    rows.push(<ClockRow key={i} index={i} apps={apps} />);
  }
  return <StyledList>{rows}</StyledList>;
};

class VirtualizedClockList extends Component {
  isItemLoaded = ({ index }) => {
    const { apps } = this.props;
    const adjustedIndex = index * CLOCKS_PER_ROW;
    return !!(
      (adjustedIndex === 0 || apps[adjustedIndex - 1]) &&
      apps[adjustedIndex]
    );
  };

  fetchMore = ({ startIndex }) => {
    if (this.props.fetchMore) {
      return this.props.fetchMore({ startIndex: startIndex * CLOCKS_PER_ROW });
    }
  };

  render() {
    const { apps, hasNextPage } = this.props;

    const clocksPerPage = appsPerPage / CLOCKS_PER_ROW;
    const clocksCount = Math.ceil(apps.length / CLOCKS_PER_ROW);
    return (
      <VirtualizedList
        currentCount={clocksCount}
        maxCount={hasNextPage === false ? clocksCount : undefined}
        pageSize={clocksPerPage}
        isItemLoaded={this.isItemLoaded}
        fetchMore={this.fetchMore}
      >
        {({ index, style }) => (
          <ClockRow index={index} style={style} apps={apps} />
        )}
      </VirtualizedList>
    );
  }
}

export default VirtualizedClockList;
